import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, useAnimation, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import HomeBlog from '../components/HomeBlog';
import AppointmentModal from '../components/AppointmentModal';
import AboutImg from '../assets/about.webp';
import doctor1 from '../assets/praveen.jpg';
import heroImg from '../assets/hero1.webp';
import hero1Img from '../assets/hero2.webp';
import hero2Img from '../assets/pravinn.webp';
import statsBg from '../assets/hero2.webp';
import testimonial1 from '../assets/testimonial1.webp';
import testimonial2 from '../assets/testimonial2.webp';
import testimonial3 from '../assets/testimonial3.webp';
import gallery1 from '../assets/gallery1.webp';
import gallery2 from '../assets/gallery2.webp';
import gallery3 from '../assets/gallery3.webp';
import gallery4 from '../assets/gallery4.webp';
import gallery5 from '../assets/gallery5.webp';
import gallery6 from '../assets/gallery6.webp';
import gallery7 from '../assets/gallery7.webp';
import gallery8 from '../assets/gallery8.webp';
import gallery9 from '../assets/gallery9.webp';
import gallery10 from '../assets/gallery10.webp';

const CountUpNumber = ({ targetNumber, suffix = '', duration = 2, startCounting = false }) => {
  const count = useMotionValue(0);
  const roundedCount = useTransform(count, (latest) => Math.floor(latest));

  useEffect(() => {
    if (startCounting) {
      const animation = animate(count, targetNumber, {
        duration: duration,
        ease: "easeOut"
      });
      return () => animation.stop();
    }
  }, [targetNumber, duration, count, startCounting]);

  return (
    <motion.span className="text-4xl md:text-5xl font-bold">
      <motion.span>{roundedCount}</motion.span>
      {suffix}
    </motion.span>
  );
};

const SectionWrapper = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay, ease: [0.16, 0.77, 0.47, 0.97] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);
  const [statsInView, setStatsInView] = useState(false);
  const heroIntervalRef = useRef(null);
  const galleryIntervalRef = useRef(null);
  const galleryContainerRef = useRef(null);
  const statsRef = useRef(null);

  const heroImages = [
    { id: 1, image: heroImg, title: "Advanced Medical Care", subtitle: "Compassionate healthcare with cutting-edge technology" },
    { id: 2, image: hero1Img, title: "Expert Specialists", subtitle: "Board-certified physicians for all your health needs" },
    { id: 3, image: hero2Img, title: "Patient-Centered Approach", subtitle: "Your wellbeing is our top priority" }
  ];

  const galleryImages = [
    { id: 1, image: gallery1, caption: "Medical Facility" },
    { id: 2, image: gallery2, caption: "Patient Care" },
    { id: 3, image: gallery3, caption: "Advanced Equipment" },
    { id: 4, image: gallery4, caption: "Expert Team" },
    { id: 5, image: gallery5, caption: "Comfortable Environment" },
    { id: 6, image: gallery6, caption: "Specialist Consultation" },
    { id: 7, image: gallery7, caption: "Modern Technology" },
    { id: 8, image: gallery8, caption: "Emergency Care" },
    { id: 9, image: gallery9, caption: "Diagnostic Center" },
    { id: 10, image: gallery10, caption: "Surgical Suite" }
  ];

  const testimonials = [
    {
      quote: "The cardiology team saved my life with their quick response and expert care. I'm forever grateful for their compassion and skill.",
      author: "Rahul Sharma",
      role: "Heart Patient",
      image: testimonial1
    },
    {
      quote: "From the moment we walked in, the pediatric staff made my child feel comfortable. The treatment was excellent and the follow-up care outstanding.",
      author: "Priya Verma",
      role: "Parent",
      image: testimonial2
    },
    {
      quote: "After my knee replacement, the physical therapy team helped me regain full mobility. The entire process was smooth and professional.",
      author: "Amit Desai",
      role: "Orthopedic Patient",
      image: testimonial3
    }
  ];

  const faqs = [
    {
      question: "What health insurance plans do you accept?",
      answer: "We accept most major insurance plans including Medicare, Blue Cross Blue Shield, Aetna, Cigna, and UnitedHealthcare. Please contact our billing department to verify your specific coverage."
    },
    {
      question: "How do I schedule an appointment?",
      answer: "You can schedule an appointment by calling our office during business hours, using our online patient portal, or through the mobile app. New patients will need to complete registration forms prior to their first visit."
    },
    {
      question: "What should I bring to my first appointment?",
      answer: "Please bring your insurance card, photo ID, list of current medications, medical records if transferring care, and any relevant test results or imaging reports. Arrive 15 minutes early to complete paperwork."
    },
    {
      question: "Do you offer telehealth services?",
      answer: "Yes, we offer secure video consultations for many types of appointments. Some conditions may require an in-person visit. Check with your provider to see if telehealth is appropriate for your needs."
    },
    {
      question: "What are your COVID-19 safety protocols?",
      answer: "We follow all CDC guidelines including enhanced cleaning, mask requirements in clinical areas, and social distancing in waiting rooms. All staff are fully vaccinated and we offer separate visiting hours for high-risk patients."
    },
    {
      question: "How can I access my medical records?",
      answer: "You can access your records through our secure patient portal 24/7. For full records requests, please submit a signed release form to our medical records department, which may take 3-5 business days to process."
    }
  ];

  const stats = [
    { id: 1, number: 25, suffix: '+', label: 'Years of Experience' },
    { id: 2, number: 10000, suffix: '+', label: 'Patients Treated' },
    { id: 3, number: 50, suffix: '+', label: 'Specialists' },
    { id: 4, number: 12, suffix: '', label: 'Departments' }
  ];

  // Auto slide for hero carousel
  useEffect(() => {
    heroIntervalRef.current = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev === heroImages.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(heroIntervalRef.current);
  }, []);

  // Improved gallery auto-slide with infinite loop
  useEffect(() => {
    galleryIntervalRef.current = setInterval(() => {
      scrollGallery('right');
    }, 3000);

    return () => clearInterval(galleryIntervalRef.current);
  }, []);

  // Stats section visibility observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsInView(true);
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  const goToHeroSlide = (index) => {
    clearInterval(heroIntervalRef.current);
    setCurrentHeroSlide(index);
    heroIntervalRef.current = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev === heroImages.length - 1 ? 0 : prev + 1));
    }, 5000);
  };

  const scrollToGallerySlide = (index) => {
    if (galleryContainerRef.current) {
      const container = galleryContainerRef.current;
      const slideWidth = container.scrollWidth / (galleryImages.length * 2);
      container.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth'
      });
    }
  };

  const scrollGallery = (direction) => {
    if (!galleryContainerRef.current) return;

    const container = galleryContainerRef.current;
    const slideWidth = container.scrollWidth / (galleryImages.length * 2);
    const currentScroll = container.scrollLeft;
    let targetScroll;

    if (direction === 'left') {
      targetScroll = currentScroll - slideWidth;
      if (targetScroll <= 0) {
        // When reaching the start, jump to the middle (clone) without animation
        container.scrollTo({
          left: galleryImages.length * slideWidth,
          behavior: 'auto'
        });
        targetScroll = (galleryImages.length * slideWidth) - slideWidth;
      }
    } else {
      targetScroll = currentScroll + slideWidth;
      if (targetScroll >= galleryImages.length * slideWidth) {
        // When reaching the end, jump to the start (clone) without animation
        container.scrollTo({
          left: 0,
          behavior: 'auto'
        });
        targetScroll = slideWidth;
      }
    }

    // Smooth scroll to the target position
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    const newSlide = Math.round(targetScroll / slideWidth) % galleryImages.length;
    setCurrentGallerySlide(newSlide);

    // Reset the auto-slide interval
    clearInterval(galleryIntervalRef.current);
    galleryIntervalRef.current = setInterval(() => {
      scrollGallery('right');
    }, 3000);
  };

  return (
    <div className="overflow-x-hidden">
      <Helmet>
        <title>The Healing | Premier Healthcare Services in Gaya, Bihar</title>
        <meta
          name="description"
          content="The Healing provides world-class healthcare services in Gaya, Bihar with expert doctors, advanced facilities, and compassionate care. Book your appointment today."
        />
        <meta name="keywords" content="hospital in Gaya, best doctors in Bihar, healthcare services, medical center, cardiology, orthopedics, pediatrics" />
        <meta property="og:title" content="The Healing | Premier Healthcare Services" />
        <meta property="og:description" content="Experience world-class healthcare at The Healing with our expert medical team and state-of-the-art facilities." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thehealing.in" />
        <link rel="canonical" href="https://thehealing.in" />
      </Helmet>

      <AppointmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Hero Section */}
      <section className="relative h-screen max-h-[800px] sm:h-[700px] h-[500px] overflow-hidden">
        <motion.div
          className="relative h-full w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative h-full w-full overflow-hidden">
            {heroImages.map((slide, index) => (
              <motion.div
                key={slide.id}
                className={`absolute inset-0 ${index === currentHeroSlide ? 'z-10' : 'z-0'}`}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: index === currentHeroSlide ? 1 : 0,
                  transition: { duration: 1 }
                }}
              >
                <img
                  src={slide.image}
                  alt={`Slide ${index + 1}`}
                  className="w-full h-full object-cover max-h-[500px] sm:max-h-none"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
              </motion.div>
            ))}

            <div className="absolute inset-0 z-20 flex items-center justify-center text-center px-4 pt-8 sm:pt-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="container mx-auto px-4 text-white"
              >
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="text-3xl sm:text-5xl font-bold mb-2 sm:mb-4"
                >
                  {heroImages[currentHeroSlide].title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="text-md sm:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto"
                >
                  {heroImages[currentHeroSlide].subtitle}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.1 }}
                  className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4"
                >
                  <motion.button
                    onClick={() => setIsModalOpen(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-2 sm:px-8 sm:py-3 bg-white text-blue-600 hover:bg-gray-100 rounded-full font-bold transition-all shadow-lg"
                  >
                    Book Appointment
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-2 sm:px-8 sm:py-3 bg-transparent border-2 border-white hover:bg-white hover:text-blue-600 rounded-full font-bold transition-all shadow-lg"
                  >
                    <Link to="/services">Our Services</Link>
                  </motion.button>
                </motion.div>
              </motion.div>
            </div>

            <div className="absolute bottom-6 sm:bottom-8 left-0 right-0 z-20 flex justify-center gap-2">
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToHeroSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${index === currentHeroSlide ? 'bg-white w-6' : 'bg-white bg-opacity-50'}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => goToHeroSlide(currentHeroSlide === 0 ? heroImages.length - 1 : currentHeroSlide - 1)}
              className="absolute left-2 sm:left-4 top-1/2 z-20 -translate-y-1/2 bg-white bg-opacity-30 hover:bg-opacity-50 text-white p-2 rounded-full transition-all"
              aria-label="Previous slide"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={() => goToHeroSlide(currentHeroSlide === heroImages.length - 1 ? 0 : currentHeroSlide + 1)}
              className="absolute right-2 sm:right-4 top-1/2 z-20 -translate-y-1/2 bg-white bg-opacity-30 hover:bg-opacity-50 text-white p-2 rounded-full transition-all"
              aria-label="Next slide"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </motion.div>
      </section>


      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <SectionWrapper delay={0.1}>
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/2 relative">
                <motion.div
                  className="relative rounded-xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img src={AboutImg} alt="Doctor with patient" className="w-full h-auto" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6 text-white">
                    <h3 className="text-xl font-bold">Compassionate Care Since 1998</h3>
                  </div>
                </motion.div>
                <motion.div
                  className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg hidden md:block"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="text-3xl font-bold text-blue-600">25+</div>
                  <div className="text-gray-600">Years of Experience</div>
                </motion.div>
              </div>

              <div className="md:w-1/2">
                <motion.h2
                  className="text-3xl md:text-4xl font-bold mb-6"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">
                    About Our Medical Center
                  </span>
                </motion.h2>
                <motion.p
                  className="text-gray-600 mb-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  Established in 1998, The Healing has grown to become a leading provider of comprehensive medical services in the region.
                </motion.p>
                <motion.p
                  className="text-gray-600 mb-8"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  Our team of over 120 specialists across 40 departments works collaboratively to provide integrated care.
                </motion.p>
                <motion.div
                  className="grid grid-cols-2 gap-4"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span className="font-medium">24/7 Emergency</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-100 rounded-full">
                      <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span className="font-medium">Board Certified</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span className="font-medium">Advanced Imaging</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-100 rounded-full">
                      <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span className="font-medium">Patient Advocacy</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </SectionWrapper>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 relative">
        <div className="absolute inset-0 z-0">
          <img
            src={statsBg}
            alt="Stats background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-70"></div>
        </div>

        <SectionWrapper delay={0.1}>
          <div className="container mx-auto px-6 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
              {stats.map((stat) => (
                <motion.div
                  key={stat.id}
                  className="p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <CountUpNumber
                    targetNumber={stat.number}
                    suffix={stat.suffix}
                    duration={2}
                    startCounting={statsInView}
                  />
                  <motion.div
                    className="text-xl mt-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: statsInView ? 1 : 0 }}
                    transition={{ delay: 1.5 }}
                  >
                    {stat.label}
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </SectionWrapper>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-white">
        <SectionWrapper delay={0.1}>
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">
                  Health Articles & News
                </span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Stay informed with the latest medical research, health tips, and wellness advice.
              </p>
            </motion.div>
            <HomeBlog />
          </div>
        </SectionWrapper>
      </section>

      {/* Team Section */}
      <section className="py-10 bg-gray-50">
        <SectionWrapper delay={0.1}>
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <motion.div
                className="md:w-1/3 lg:w-1/2"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative rounded-xl overflow-hidden shadow-2xl">
                  <img
                    src={doctor1}
                    alt="Dr. Pravin Gauswami"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>

              <motion.div
                className="md:w-2/3 lg:w-1/2"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">
                    Meet Our Founder
                  </span>
                </h2>
                <h3 className="text-2xl font-bold mb-2">Dr. Pravin Gauswami</h3>
                <p className="text-blue-600 font-medium mb-6">Founder of The Healing</p>

                <p className="text-gray-600 mb-4">
                  With over 25 years of experience in cardiology, Dr. Pravin Gauswami established The Healing with a vision to provide compassionate care.
                </p>
                <p className="text-gray-600 mb-6">
                  Dr. Gauswami completed his medical training at prestigious institutions and has been recognized with numerous awards.
                </p>

                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Cardiology Specialist</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>25+ Years Experience</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Patient Advocacy</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </SectionWrapper>
      </section>

      {/* Gallery Section */}
      <section className="py-10 bg-white relative">
        <SectionWrapper delay={0.1}>
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">
                  Our Gallery
                </span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                A visual journey through our facilities and services.
              </p>
            </motion.div>

            <div className="relative">
              <button
                onClick={() => scrollGallery('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all"
                aria-label="Scroll gallery left"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => scrollGallery('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all"
                aria-label="Scroll gallery right"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <div
                ref={galleryContainerRef}
                className="flex overflow-x-auto snap-x snap-mandatory gap-6 py-4 px-8 scroll-smooth"
                style={{
                  scrollSnapType: 'x mandatory',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                <style dangerouslySetInnerHTML={{ __html: `.overflow-x-auto::-webkit-scrollbar { display: none; }` }} />

                {[...galleryImages, ...galleryImages].map((item, index) => (
                  <motion.div
                    key={`${item.id}-${index}`}
                    className="flex-shrink-0 w-80 h-96 snap-center relative rounded-xl overflow-hidden shadow-lg"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: (index % galleryImages.length) * 0.1 }}
                  >
                    <motion.img
                      src={item.image}
                      alt={item.caption}
                      className="w-full h-full object-cover"
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.5 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent flex items-end p-6">
                      <div className="text-white">
                        <h3 className="text-xl font-bold">{item.caption}</h3>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex justify-center mt-6 gap-2">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      const adjustedIndex = index % galleryImages.length;
                      setCurrentGallerySlide(adjustedIndex);
                      scrollToGallerySlide(adjustedIndex);
                    }}
                    className={`w-3 h-3 rounded-full transition-all ${index === currentGallerySlide ? 'bg-blue-600 w-6' : 'bg-gray-300'}`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </SectionWrapper>
      </section>

      {/* Testimonials Section */}
      <section className="py-10 bg-gray-50">
        <SectionWrapper delay={0.1}>
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">
                  Patient Experiences
                </span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Hear from our patients about their journeys to better health.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="bg-white p-8 rounded-xl shadow-md relative transition-all hover:shadow-lg"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="absolute top-0 left-8 -translate-y-1/2 bg-blue-500 text-white p-3 rounded-full">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                    </svg>
                  </div>
                  <p className="text-gray-600 italic mb-6">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-gray-200 mr-4 overflow-hidden">
                      <img
                        src={testimonial.image}
                        alt={testimonial.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="font-bold">{testimonial.author}</h4>
                      <p className="text-gray-500 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </SectionWrapper>
      </section>

      {/* FAQ Section */}
      <section className="py-10 bg-white">
        <SectionWrapper delay={0.1}>
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">
                  Frequently Asked Questions
                </span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Get answers to common questions about our services.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  className="mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <details className="group bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <summary className="flex justify-between items-center font-medium cursor-pointer list-none">
                      <span className="text-gray-900">{faq.question}</span>
                      <span className="transition group-open:rotate-180">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </span>
                    </summary>
                    <motion.p
                      className="mt-3 text-gray-600"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {faq.answer}
                    </motion.p>
                  </details>
                </motion.div>
              ))}
            </div>
          </div>
        </SectionWrapper>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-blue-800">
        <SectionWrapper delay={0.1}>
          <motion.div
            className="max-w-4xl mx-auto text-center text-white"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif leading-tight">Begin Your Journey to Better Health</h2>
            <p className="text-xl mb-8">Our patient care coordinators are available 24/7 to assist you</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.button
                onClick={() => setIsModalOpen(true)}
                className="bg-white hover:bg-gray-100 text-blue-800 font-bold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Schedule Consultation
              </motion.button>
              <motion.a
                href="tel:+919430003381"
                className="bg-transparent hover:bg-white hover:text-blue-800 text-white font-bold py-3 px-8 border-2 border-white rounded-lg transition-all duration-300 shadow-lg inline-block text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Emergency: +91 9430003381
              </motion.a>
            </div>
            <p className="mt-6 text-blue-100">
              Or visit us at: Wazirganj Gaya Bihar India - 803051
            </p>
          </motion.div>
        </SectionWrapper>
      </section>
    </div>
  );
};

export default HomePage;